import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useSearchParams, Navigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { SubscribeForm } from "../components/SubscribeForm";
import { useKahaniDrift, KahaniDriftButton } from "../components/KahaniDrift";
import { BINGE_SERIES, loadResume, saveResume, clearResume, track } from "../lib/binge";

const AHEAD = 2;
const BEHIND = 1;
const DEFAULT_RATIO = "1152 / 2048";
const HEADER_H = 57;
const fmt = (n) => String(n);

function buildEntries(chapters, details) {
  const out = [];
  chapters.forEach((c, ci) => {
    const d = details[ci];
    if (d.status !== "fulfilled") {
      out.push({ ch: c.chapter_number, idx: 0, title: true, failed: true, url: c.thumbnail_url, alt: c.title || "" });
      return;
    }
    d.value.forEach((p, idx) => out.push({ ch: c.chapter_number, idx, title: idx === 0, url: p.image_url, alt: p.alt_text || "" }));
  });
  return out;
}

const BingePanel = React.forwardRef(({ entry, i, active, ratio, onRatio, slug }, ref) => (
  <div
    ref={ref}
    data-testid={`binge-panel-${i}`}
    data-chapter={fmt(entry.ch)}
    data-title={entry.title ? "1" : undefined}
    data-drift-pause={entry.title && i > 0 ? "1" : undefined}
    data-words={entry.alt.split(/\s+/).filter(Boolean).length}
    className="relative w-full bg-[#111]"
    style={{ aspectRatio: ratio || DEFAULT_RATIO }}
  >
    {active && entry.url && (
      <img
        src={resolveImg(entry.url)}
        alt={entry.alt}
        draggable={false}
        className="block w-full h-full object-contain select-none"
        onLoad={(e) => onRatio(i, `${e.currentTarget.naturalWidth} / ${e.currentTarget.naturalHeight}`)}
      />
    )}
    {entry.failed && (
      <div className="absolute inset-x-0 bottom-6 flex justify-center">
        <Link
          to={`/series/${slug}/chapter/${fmt(entry.ch)}`}
          data-testid={`binge-failed-link-${fmt(entry.ch)}`}
          className="bg-black/70 text-white text-sm px-4 py-2 rounded-full border border-white/20"
        >
          Couldn't load this episode here — open Episode {fmt(entry.ch)}
        </Link>
      </div>
    )}
  </div>
));

export default function BingeReader() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [series, setSeries] = useState(null);
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(false);
  const [cur, setCur] = useState(0);
  const [ratios, setRatios] = useState({});
  const refs = useRef([]);
  const laneRef = useRef(null);
  const endRef = useRef(null);
  const seenTitles = useRef(new Set());
  const done = useRef(false);
  const started = useRef(false);
  const [ready, setReady] = useState(false);
  const drift = useKahaniDrift(laneRef, endRef, slug, { panelSelector: '[data-testid^="binge-panel-"]', offset: 57 });

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/series/${slug}`)
      .then(async ({ data }) => {
        const chapters = [...(data.chapters || [])].sort((a, b) => a.chapter_number - b.chapter_number);
        const details = await Promise.allSettled(
          chapters.map((c) => api.get(`/series/${slug}/chapter/${fmt(c.chapter_number)}`).then((r) => r.data.panels || []))
        );
        if (cancelled) return;
        setSeries(data);
        setEntries(buildEntries(chapters, details));
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Initial position (resume) + binge_start.
  useEffect(() => {
    if (!entries || started.current) return;
    started.current = true;
    const saved = params.get("resume") ? loadResume(slug) : null;
    const at = saved ? entries.findIndex((e) => e.ch === saved.chapter && e.idx === saved.panel) : -1;
    track("binge_start", { series: slug, resumed: at > 0, start_episode: at > 0 ? entries[at].ch : entries[0]?.ch });
    if (at > 0) {
      setCur(at);
      requestAnimationFrame(() => {
        const el = refs.current[at];
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - HEADER_H, behavior: "instant" });
        setReady(true);
      });
    } else {
      setReady(true);
    }
  }, [entries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track the panel at the viewport centre.
  useEffect(() => {
    if (!entries) return;
    let ticking = false;
    const measure = () => {
      ticking = false;
      const mid = window.innerHeight / 2;
      const i = refs.current.findIndex((el) => {
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return r.top <= mid && r.bottom > mid;
      });
      if (i >= 0) setCur(i);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(measure);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [entries]);

  const lastViewed = useCallback(() => {
    const e = entries?.[cur];
    if (e) track("last_panel_viewed", { series: slug, episode: e.ch, panel: e.idx + 1, transport_type: "beacon" });
  }, [entries, cur, slug]);

  // Resume save, per-episode title analytics, completion.
  useEffect(() => {
    const e = entries?.[cur];
    if (!ready || !e || done.current) return;
    if (e.title && !seenTitles.current.has(e.ch)) {
      seenTitles.current.add(e.ch);
      track("episode_title_panel_viewed", { series: slug, episode: e.ch });
    }
    if (cur === entries.length - 1) {
      done.current = true;
      clearResume(slug);
      track("binge_complete", { series: slug, episodes: new Set(entries.map((x) => x.ch)).size });
      lastViewed();
      return;
    }
    if (cur > 0) saveResume(slug, e.ch, e.idx);
  }, [cur, entries, slug, lastViewed, ready]);

  useEffect(() => {
    const onHide = () => document.visibilityState === "hidden" && lastViewed();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", lastViewed);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", lastViewed);
    };
  }, [lastViewed]);

  const pendingShift = useRef([]);
  const onRatio = useCallback((i, r) => {
    const el = refs.current[i];
    if (el && el.getBoundingClientRect().bottom <= HEADER_H + 2) pendingShift.current.push({ el, before: el.offsetHeight });
    setRatios((m) => (m[i] === r ? m : { ...m, [i]: r }));
  }, []);

  // Keep the reader's view steady when a panel above it settles to its real size.
  useLayoutEffect(() => {
    const items = pendingShift.current;
    pendingShift.current = [];
    const delta = items.reduce((sum, { el, before }) => sum + (el.offsetHeight - before), 0);
    if (delta) window.scrollBy({ top: delta, behavior: "instant" });
  }, [ratios]);
  const lastCh = useMemo(() => entries?.[entries.length - 1]?.ch, [entries]);

  if (!BINGE_SERIES.includes(slug)) return <Navigate to={`/series/${slug}`} replace />;

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="binge-reader">
      <header className="sticky top-0 z-40 bg-[#0B0C10]/90 backdrop-blur border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to={`/series/${slug}`} data-testid="binge-back-btn" className="text-gray-300 hover:text-white p-2 -ml-2">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="min-w-0">
            <p className="text-white font-semibold truncate">{series?.title || "Loading…"}</p>
            <p className="text-[11px] font-mono-kh uppercase tracking-widest text-gray-500" data-testid="binge-position">
              {entries?.[cur] ? `All episodes · Episode ${fmt(entries[cur].ch)}` : "All episodes"}
            </p>
          </div>
        </div>
      </header>

      <main className="pb-24">
        {error ? (
          <p className="text-center text-gray-400 py-24" data-testid="binge-error">Couldn't load episodes. Please try again.</p>
        ) : !entries ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 text-rose-500 animate-spin" /></div>
        ) : (
          <>
            <div className="flex justify-center py-3" data-testid="kahani-drift-bar">
              <KahaniDriftButton state={drift.state} onToggle={drift.toggle} />
            </div>
            <div ref={laneRef} className="reader-lane" data-testid="binge-lane">
              {entries.map((e, i) => (
                <BingePanel
                  key={`${e.ch}-${e.idx}`}
                  ref={(el) => (refs.current[i] = el)}
                  entry={e}
                  i={i}
                  slug={slug}
                  active={i >= cur - BEHIND && i <= cur + AHEAD}
                  ratio={ratios[i]}
                  onRatio={onRatio}
                />
              ))}
            </div>
            <div ref={endRef} data-testid="binge-end" className="max-w-3xl mx-auto px-4 mt-10 text-center">
              <p className="font-display text-2xl text-white font-bold">You're all caught up</p>
              <p className="text-gray-400 mt-2" data-testid="binge-next-drop">
                Next drop: Episode {fmt(Math.floor(lastCh) + 1)} is on its way. Get it in your inbox the moment it lands.
              </p>
              <div className="mt-6 text-left">
                <SubscribeForm source={`${slug} binge`} />
              </div>
            </div>
          </>
        )}
      </main>

      {drift.state !== "off" && (
        <div className="fixed right-3 bottom-6 z-50 opacity-80 hover:opacity-100 transition-opacity" data-testid="kahani-drift-floating">
          <KahaniDriftButton state={drift.state} onToggle={drift.toggle} testId="kahani-drift-floating-btn" compact />
        </div>
      )}
    </div>
  );
}
